import * as React from 'react';

import { Input, InputProps } from '@lightspeed/flame/Input';
import { UseMutationResult } from '@tanstack/react-query';
import axios from 'axios';

import { SENSITIVE_INFO_WARNING } from 'root/merchantos/utils';
import { hasCreditCardInfo } from 'root/regexValidation';

export interface RowEditInputStringProps extends InputProps {
  value: string;
  fieldName: string;
  mutationUpdate: UseMutationResult<any, unknown, any>;
  panDetection?: boolean;
}

export const RowEditInputString: React.FC<RowEditInputStringProps> = ({
  value,
  fieldName,
  mutationUpdate,
  panDetection = false,
  ...rest
}): React.ReactElement => {
  const [inputValue, setInputValue] = React.useState(value);
  const [parsingError, setParsingError] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  React.useEffect(() => {
    if (panDetection && hasCreditCardInfo(inputValue)) {
      setParsingError(SENSITIVE_INFO_WARNING);
      return;
    }
    setParsingError(undefined);
  }, [inputValue, panDetection, mutationUpdate]);

  const saveValue = () => {
    const parsedValue = inputValue.trim();
    if (parsedValue === value) {
      return;
    }
    mutationUpdate.mutate({
      [fieldName]: parsedValue,
    });
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      saveValue();
    }
  };
  const handleBlur = (_event: React.FocusEvent<HTMLInputElement>) => {
    saveValue();
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (mutationUpdate.isError) mutationUpdate.reset();
    setInputValue(event.target.value);
  };

  React.useEffect(() => {
    if (mutationUpdate.isLoading) {
      window.showNotifier('working');
    } else if (mutationUpdate.isSuccess) {
      window.hideNotifier('working');
      window.flashNotifier('success', 'Saved Changes', 3);
    } else if (mutationUpdate.isError) {
      window.hideNotifier('working');
    }
    // only update the notifier if the loading state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutationUpdate.isLoading]);

  const errorMessage = React.useMemo(() => {
    if (mutationUpdate.isError) {
      if (axios.isAxiosError(mutationUpdate.error)) {
        if (mutationUpdate.error.response?.data?.message)
          return mutationUpdate.error.response.data.message;
        if (mutationUpdate.error.response?.data?.errors?.[0]?.detail)
          return mutationUpdate.error.response.data.errors[0].detail;
      }
      return 'Something went wrong. Try again.';
    }
    return undefined;
  }, [mutationUpdate.error, mutationUpdate.isError]);

  return (
    <Input
      autoComplete="off"
      value={inputValue}
      onChange={handleChange}
      onKeyPress={handleKeyPress}
      onBlur={handleBlur}
      disabled={mutationUpdate.isLoading}
      status={mutationUpdate.isError || parsingError ? 'error' : undefined}
      statusMessage={errorMessage || parsingError}
      {...rest}
    />
  );
};
